<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen"
                 @click.self="$emit('close')"
                 class="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 z-[51]"
            >
                <div class="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden relative max-h-[90vh] overflow-y-auto">

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/20 hover:bg-black/30 transition-colors"
                    >
                        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <!-- Header Section -->
                    <div class="bg-amber-500 px-8 py-6 text-center">
                        <img
                            :src="STAR_ICON"
                            alt="Star"
                            class="h-14 mx-auto mb-3 drop-shadow-lg"
                        />
                        <h2 class="text-3xl font-bold text-white mb-1.5">
                            Get More Stars
                        </h2>
                        <p class="text-white/95 text-sm leading-relaxed max-w-xs mx-auto">
                            Purchase star packs to sponsor your favorite auditions
                        </p>
                    </div>

                    <!-- Current Balance -->
                    <div class="flex items-center justify-center gap-3 py-5 bg-white border-b border-gray-100">
                        <span class="text-sm text-gray-500 font-medium">Your Balance</span>
                        <div class="flex items-center gap-1.5">
                            <img :src="STAR_ICON" alt="" class="w-6 h-6" />
                            <span class="text-3xl font-bold text-gray-900">{{ userBalance }}</span>
                        </div>
                    </div>

                    <!-- Star Packs -->
                    <div class="px-6 pt-5 pb-3">
                        <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                            Choose a Star Pack
                        </p>

                        <div class="grid grid-cols-3 gap-2.5">
                            <button
                                v-for="pack in packs"
                                :key="pack.stars"
                                @click="handlePackSelect(pack)"
                                :class="[
                                    'relative py-4 px-2 rounded-2xl border-2 transition-all active:scale-[0.97]',
                                    selectedPack && selectedPack.stars === pack.stars
                                        ? 'border-amber-500 bg-gray-900 ring-4 ring-amber-500/20'
                                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg'
                                ]"
                            >
                                <div class="flex flex-col items-center gap-1.5">
                                    <img :src="STAR_ICON" alt="" class="w-7 h-7" />
                                    <span :class="[
                                        'text-lg font-bold',
                                        selectedPack && selectedPack.stars === pack.stars ? 'text-white' : 'text-gray-900'
                                    ]">{{ pack.stars.toLocaleString() }}</span>
                                    <span :class="[
                                        'text-xs font-semibold',
                                        selectedPack && selectedPack.stars === pack.stars ? 'text-gray-400' : 'text-gray-500'
                                    ]">${{ pack.price.toFixed(2) }}</span>
                                </div>
                            </button>
                        </div>
                    </div>

                    <!-- CTA Button -->
                    <div ref="ctaRef" class="px-6 pb-4 pt-2">
                        <button
                            @click="handleBuy"
                            :disabled="!selectedPack"
                            :class="[
                                'w-full py-4 font-bold text-lg rounded-xl transition-all active:scale-[0.99]',
                                selectedPack
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            ]"
                        >
                            {{ buyButtonText }}
                        </button>
                    </div>

                    <!-- Trust Indicators -->
                    <div class="px-6 pb-6">
                        <div class="flex items-center justify-center gap-3 text-xs text-gray-500 pt-2">
                            <span class="flex items-center gap-1">
                                <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                                </svg>
                                Secure Payment
                            </span>
                            <span class="text-gray-300">&bull;</span>
                            <span>Instant Delivery</span>
                            <span class="text-gray-300">&bull;</span>
                            <span>No Subscription</span>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    userBalance: {
        type: Number,
        default: 0
    },
    starPacks: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['close', 'buy'])

const selectedPack = ref(null)
const ctaRef = ref(null)

const defaultStarPacks = [
    { stars: 999, price: 9.99, purchaseUrl: null },
    { stars: 80, price: 19.99, purchaseUrl: null },
    { stars: 160, price: 39.99, purchaseUrl: null },
    { stars: 320, price: 79.99, purchaseUrl: null },
    { stars: 600, price: 149.99, purchaseUrl: null },
    { stars: 800, price: 199.99, purchaseUrl: null },
    { stars: 4000, price: 999.99, purchaseUrl: null },
]

const packs = computed(() => props.starPacks && props.starPacks.length > 0 ? props.starPacks : defaultStarPacks)

// Reset selection when modal opens
watch(() => props.isOpen, (open) => {
    if (open) selectedPack.value = null
})

const handlePackSelect = (pack) => {
    selectedPack.value = selectedPack.value?.stars === pack.stars ? null : pack
    if (selectedPack.value) {
        nextTick(() => {
            ctaRef.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
    }
}

const buyButtonText = computed(() => {
    if (!selectedPack.value) return 'Select a Pack'
    const p = selectedPack.value
    return `Buy ${p.stars.toLocaleString()} Stars for $${p.price.toFixed(2)}`
})

const handleBuy = () => {
    if (!selectedPack.value) return
    emit('buy', selectedPack.value)
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .max-w-md,
.modal-leave-active .max-w-md {
    transition: transform 0.3s ease;
}

.modal-enter-from .max-w-md,
.modal-leave-to .max-w-md {
    transform: scale(0.95);
}
</style>
